import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { SafeAreaView, StyleSheet, Text, TouchableOpacity, View, Pressable, ScrollView, Animated, Easing } from "react-native";
import React, { useState, useEffect, useRef } from "react";
import CampTypes from "../../../../data/CampTypes";
import FilterModalRangeFilter from "./FilterModalRangeFilter";
import FilterModalCategories from "./FilterModalCategories";

const CampFilterModal = (
    {
        cachedCamps,
        setCamps,
        handleModalClose,
        selectedCategories,
        setSelectedCategories,
        ratingRange,
        setRatingRange,
        distanceRange,
        setDistanceRange,
        showActiveSessions,
        setShowActiveSessions,
    }
) => {

    const [filteredCamps, setFilteredCamps] = useState(cachedCamps);
    const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);

    // Animated toggle for Show Active SnapCamps
    const showActiveAnim = useRef(new Animated.Value(showActiveSessions ? 1 : 0)).current;
    useEffect(() => {
        Animated.timing(showActiveAnim, {
            toValue: showActiveSessions ? 1 : 0,
            duration: 220,
            easing: Easing.out(Easing.quad),
            useNativeDriver: false,
        }).start();
    }, [showActiveSessions]);
    const thumbTranslateX = showActiveAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [2, 26],
    });

    useEffect(() => {
        setFilteredCamps(cachedCamps);

        if (cachedCamps && cachedCamps.length > 0) {
            let filtered = [...cachedCamps];

            if (selectedCategories.length > 0) {
                filtered = filtered.filter(camp => selectedCategories.map(text => CampTypes.find(type => type.text === text)?.iconName).filter(Boolean).includes(camp.campCategory));
            }

            if (distanceRange[0] > 0 || distanceRange[1] < 300) {
                filtered = filtered.filter(camp => {
                    if (camp.distance === undefined || camp.distance === null) {
                        return true;
                    }
                    return camp.distance >= distanceRange[0] && camp.distance <= distanceRange[1];
                });
            }

            if (ratingRange[0] > 0 || ratingRange[1] < 5) {
                filtered = filtered.filter(camp => {
                    const rating = camp.rating || 5.0;
                    return rating >= ratingRange[0] && rating <= ratingRange[1];
                });
            }

            if (showActiveSessions) {
                filtered = filtered.filter(camp => {
                    return camp.activeSessionCount > 0;
                });
            }

            setFilteredCamps(filtered);
        }
    }, [cachedCamps, selectedCategories, distanceRange, ratingRange, showActiveSessions]);

    const clearFilters = () => {
        setDistanceRange([0, 300]);
        setRatingRange([0, 5]);
        setShowActiveSessions(false);
        setSelectedCategories([]);
        setCamps(cachedCamps);
        setFilteredCamps(cachedCamps);
    }

    const filterCamps = (selectedCategories, distanceRangeValue = distanceRange, ratingRangeValue = ratingRange, showActiveSessions) => {
        if (!cachedCamps || cachedCamps.length === 0) {
            return;
        }

        let filtered = [...cachedCamps];

        if (selectedCategories.length > 0) {
            filtered = filtered.filter(camp => selectedCategories.map(text => CampTypes.find(type => type.text === text)?.iconName).filter(Boolean).includes(camp.campCategory));
        }

        if (distanceRangeValue[0] > 0 || distanceRangeValue[1] < 300) {
            filtered = filtered.filter(camp => {
                if (camp.distance === undefined || camp.distance === null) {
                    return true;
                }
                return camp.distance >= distanceRangeValue[0] && camp.distance <= distanceRangeValue[1];
            });
        }

        if (ratingRangeValue[0] > 0 || ratingRangeValue[1] < 5) {
            filtered = filtered.filter(camp => {
                const rating = camp.rating || 5.0;
                return rating >= ratingRangeValue[0] && rating <= ratingRangeValue[1];
            });
        }

        if (showActiveSessions) {
            filtered = filtered.filter(camp => {
                return camp.activeSessionCount > 0;
            });
        }

        setFilteredCamps(filtered);
    };

    const toggleCategory = (category) => {
        const newCategories = selectedCategories.includes(category)
            ? selectedCategories.filter(item => item !== category)
            : [...selectedCategories, category];

        setSelectedCategories(newCategories);
        filterCamps(newCategories, distanceRange, ratingRange, showActiveSessions);
    };

    const toggleUpcomingSessions = () => {
        const newValue = !showActiveSessions;
        setShowActiveSessions(newValue);
        filterCamps(selectedCategories, distanceRange, ratingRange, newValue);
    };

    const onFilter = () => {
        setCamps(filteredCamps);
        handleModalClose();
    }

    return (
        <SafeAreaView style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 5}}>
                <TouchableOpacity onPress={handleModalClose} style={{ position: 'absolute', left: 0 }}>
                    <MaterialCommunityIcons name="close" size={20} color="#111827" />
                </TouchableOpacity>
                <Text style={{ fontSize: 18, fontWeight: '700', color: '#111827' }}>Camp Filters</Text>
            </View>
            <View style={styles.divider} />

            <ScrollView style={styles.container}>
                <View style={[styles.compactToggleContainer, { paddingVertical: 12, paddingHorizontal: 16 }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                        <MaterialCommunityIcons name="lightning-bolt" size={18} color='#3C8248' />
                        <Text style={[styles.compactToggleLabel, { fontWeight: '700', marginLeft: 8 }]}>Show Active SnapCamps</Text>
                    </View>
                    <Pressable
                        onPress={toggleUpcomingSessions}
                        style={({ pressed }) => ([
                            {
                                width: 56,
                                height: 32,
                                borderRadius: 18,
                                justifyContent: 'center',
                                backgroundColor: showActiveSessions ? '#3C8248' : '#E5E7EB',
                                borderWidth: showActiveSessions ? 0 : 1,
                                borderColor: '#D1D5DB',
                                shadowColor: '#0EA5E9',
                                shadowOpacity: showActiveSessions ? 0.35 : 0.15,
                                shadowRadius: showActiveSessions ? 8 : 4,
                                shadowOffset: { width: 0, height: 2 },
                                opacity: pressed ? 0.9 : 1,
                            }
                        ])}
                    >
                        <View style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            borderRadius: 18,
                            overflow: 'hidden'
                        }}>
                            <View style={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, height: '50%',
                                backgroundColor: 'rgba(255,255,255,0.14)'
                            }}/>
                        </View>

                        <Animated.View
                            style={[
                                {
                                    width: 28,
                                    height: 28,
                                    borderRadius: 14,
                                    backgroundColor: '#FFFFFF',
                                    transform: [{ translateX: thumbTranslateX }],
                                    shadowColor: '#000',
                                    shadowOpacity: 0.18,
                                    shadowRadius: 4,
                                    shadowOffset: { width: 0, height: 2 },
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }
                            ]}
                        >
                            <MaterialCommunityIcons
                                name={showActiveSessions ? 'check' : 'minus'}
                                size={16}
                                color={showActiveSessions ? '#3C8248' : '#9CA3AF'}
                            />
                        </Animated.View>
                    </Pressable>
                </View>

                <FilterModalCategories
                    selectedCategories={selectedCategories}
                    onChange={(newCategories) => {
                        setSelectedCategories(newCategories);
                        filterCamps(newCategories, distanceRange, ratingRange, showActiveSessions);
                    }}
                />

                <FilterModalRangeFilter
                    fieldName="Rating"
                    fieldValue={ratingRange}
                    fieldValueSetter={(value) => {
                        setRatingRange(value);
                        filterCamps(selectedCategories, distanceRange, value, showActiveSessions);
                    }}
                    min={0}
                    max={5}
                    step={0.5}
                    unitName=" stars"
                />

                <FilterModalRangeFilter
                    fieldName="Distance"
                    fieldValue={distanceRange}
                    fieldValueSetter={(value) => {
                        setDistanceRange(value);
                        filterCamps(selectedCategories, value, ratingRange, showActiveSessions);
                    }}
                    min={0}
                    max={300}
                    step={1}
                    unitName=" miles"
                />

                <View style={{ marginBottom: 20, paddingHorizontal: 10 }}>
                    <Text style={{
                        fontSize: 12,
                        color: '#666',
                        fontStyle: 'italic',
                        textAlign: 'center'
                    }}>
                        Note: Distance filtering is limited when location data is not available
                    </Text>
                </View>
            </ScrollView>

            <View style={[styles.divider, { marginTop: 20 }]} />
            <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 10 }}>
                <Pressable onPress={clearFilters} style={{ position: 'absolute', left: 0 }}>
                    <Text style={{ fontSize: 18, fontWeight: '600' }}>Clear All</Text>
                </Pressable>

                <Pressable style={styles.showButton} onPress={onFilter}>
                    <Text style={styles.logoutButtonText}>Show {filteredCamps.length} camps</Text>
                </Pressable>
            </View>

        </SafeAreaView>
    )
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        padding: 20,
    },
    title: {
        marginTop: 10,
        flexDirection: 'row',
        alignItems: 'center',
        fontWeight: 'bold',
        fontSize: 25,
        marginBottom: 20,
    },
    field: {
        fontSize: 18,
        fontWeight: 'bold',
    },
    
    showButton: {
        backgroundColor: '#3C8248',
        paddingVertical: 14,
        paddingHorizontal: 32,
        borderRadius: 999,
        minWidth: '35%',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOpacity: 0.12,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 6 },
        elevation: 4,
    },
    logoutButtonText: {
        color: '#ffffff',
        fontSize: 15,
        fontWeight: '600',
    },
    priceBanner: {
        padding: 10,
        borderRadius: 5,
        backgroundColor: 'white',
        borderWidth: 1,
        borderColor: '#ccc',
        marginRight: 5,
    },
    divider: {
        height: 1,
        backgroundColor: '#E5E7EB',
        marginBottom: 5,
        marginTop: 5,
    },
    toggleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    toggleLabel: {
        fontSize: 16,
        fontWeight: '500',
        color: 'black',
    },
    compactToggleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginTop: 25,
        marginBottom: 35,
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    compactToggleLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: '#111827',
        marginRight: 15,
        flex: 1,
    },
    coolToggle: {
        width: 50,
        height: 28,
        borderRadius: 14,
        backgroundColor: 'black',
        padding: 2,
        justifyContent: 'center',
    },
    coolToggleActive: {
        backgroundColor: '#3C8248',
    },
    toggleThumb: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 3,
        elevation: 3,
    },
    toggleThumbActive: {
        transform: [{ translateX: 22 }],
    },
    statusText: {
        fontSize: 14,
        fontWeight: '400',
        color: '#6B7280',
        fontStyle: 'italic',
    },
});

export default CampFilterModal;